import { current } from "@reduxjs/toolkit";
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import OneSignal from "react-onesignal";

import CancelButton from "src/Components/Common/cancelbutton";
import BlueButton from "src/Components/Common/bluebutton";
import UploadButton from "./uploadbutton";
import UserService from "src/Services/API/user.service";
import { API_URL, handleError } from "src/Common/function";
import PhoneNumberInput from "src/Components/Authentication/PhoneNumberInput";
import { getImageUrl, getAvatarUrl } from "src/Common/common";

function Account() {
  const navigate = useNavigate();
  const inputFile = useRef(null);
  const [name, setName] = useState(UserService.getCurrentUser().name);
  const [phone, setPhone] = useState(UserService.getCurrentUser().phone || "");
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  const [isSMSAccept, setIsSMSAccept] = useState(false);
  const [preview, setPreview] = useState(getImageUrl("/images/common/avatar_default.png"));
  const [uploading, setUploading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushToggling, setPushToggling] = useState(false);

  useEffect(() => {
    // Own profile via the auth-only /me endpoint — getUser (get-a-user-by-id)
    // is user:read-gated and would 403 users without that permission.
    const getUser = async () => {
      try {
        const response = await UserService.getMe();
        const user = response.data;
        setName(user.name);
        setPhone(user.phone || "");
        if (user.avatar) {
          setPreview(getAvatarUrl(user.avatar));
        }
      } catch (error) {
        handleError(error);
      }
    };
    getUser();
  }, []);

  useEffect(() => {
    const syncPushState = () => {
      setPushEnabled(
        Boolean(OneSignal.Notifications?.permission) && Boolean(OneSignal.User?.PushSubscription?.optedIn),
      );
    };
    syncPushState();
    OneSignal.User.PushSubscription.addEventListener("change", syncPushState);
    OneSignal.Notifications.addEventListener("permissionChange", syncPushState);
    return () => {
      OneSignal.User.PushSubscription.removeEventListener("change", syncPushState);
      OneSignal.Notifications.removeEventListener("permissionChange", syncPushState);
    };
  }, []);

  const togglePushNotification = async () => {
    if (pushToggling) return;
    setPushToggling(true);
    try {
      if (pushEnabled) {
        await OneSignal.User.PushSubscription.optOut();
        setPushEnabled(false);
        toast("Push notifications disabled");
      } else {
        if (!OneSignal.Notifications.permission) {
          await OneSignal.Notifications.requestPermission();
        }
        if (!OneSignal.Notifications.permission) {
          toast("Please allow notifications in your browser settings");
          return;
        }
        await OneSignal.User.PushSubscription.optIn();
        // Linking the subscription to the user's email (external_id) is required
        // for the backend to target them; opt-in alone is not enough.
        const user = UserService.getCurrentUser();
        if (user?.email) {
          await OneSignal.login(user.email);
        }
        setPushEnabled(true);
        toast("Push notifications enabled");
      }
    } catch (error) {
      console.log("togglePushNotification error: ", error);
      toast("Something went wrong");
    } finally {
      setPushToggling(false);
    }
  };

  const saveChanges = () => {
    if (phoneInvalid) {
      toast("Please enter a valid phone number");
      return;
    }
    const currentUser = UserService.getCurrentUser();
    currentUser.name = name;
    currentUser.phone = phone;
    UserService.updateUser(currentUser)
      .then((response) => {
        if (response.status == 201) {
          toast("saved successfully");
          UserService.saveCurrentUser(response.data);
        } else {
          toast("Something went wrong");
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const nameChanged = (e) => {
    setName(e.target.value);
  };

  const uploadClicked = () => {
    if (uploading) return;
    inputFile.current.click();
  };

  const fileSelected = async (e) => {
    const avatar = e.target.files[0];
    // Clear the input before doing anything else: picking the same file twice in
    // a row fires no change event otherwise, so retrying after a failed upload —
    // or after a rejected size — would silently do nothing.
    e.target.value = "";
    if (!avatar) return;
    if (avatar.size > 5242880) {
      toast("File size cannot exceed more than 5MB");
      return;
    }

    // Show the picked image straight away, but keep the old one to fall back on:
    // the upload happens on its own now, so a failure has no separate button for
    // the user to notice it on, and a preview left showing an image the server
    // never took would be a lie.
    const previous = preview;
    setPreview(URL.createObjectURL(avatar));
    setUploading(true);
    try {
      const response = await UserService.uploadAvatar(avatar, UserService.getCurrentUser().id);
      if (response.status == 201) {
        toast("Photo uploaded successfully");
        UserService.saveCurrentUser(response.data);
      } else {
        setPreview(previous);
        toast("Something went wrong");
      }
    } catch (error) {
      setPreview(previous);
      handleError(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-5">
      <div className="flex items-center justify-between">
        <span className="font-OpenSansRegular text-[16px] font-[600] text-titleColor">ACCOUNT INFORMATION</span>
        <div className="flex items-center gap-5">
          <CancelButton onClicked={() => navigate(-1)} />
          <BlueButton onClicked={saveChanges} title="SAVE CHANGES" />
        </div>
      </div>

      <div className="flex items-center w-full gap-4 mt-5">
        <div className="h-[60px] w-[50%]">
          <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Full name</p>
          <div className="w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px]">
            <input
              value={name}
              type="text"
              onChange={nameChanged}
              className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
              placeholder="ex: John Smith"
            />
          </div>
        </div>
        <div className="h-[60px] w-[50%]">
          <PhoneNumberInput label="Phone" value={phone} onChange={setPhone} onInvalidChange={setPhoneInvalid} />
          {/* <div className="w-full flex items-center mt-2 gap-2">
            <input
              value={isSMSAccept}
              type="checkbox"
              onChange={setIsSMSAccept}
              className="text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
            />
            <p className="text-[12px] font-OpenSansMedium font-[500] text-titleColor">
              I would like to receive SMS notifications
            </p>
          </div> */}
        </div>
      </div>

      <div className="flex items-center gap-5 mt-10">
        <img src={preview} className="w-[200px] h-[200px] object-contain rounded-[100px]" />
        <input
          onChange={fileSelected}
          type="file"
          id="file"
          ref={inputFile}
          style={{ display: "none" }}
          multiple={false}
          accept="image/*"
        />

        <div className="w-[222px] h-[65px] flex flex-col justify-between">
          <UploadButton onClicked={uploadClicked} isLoading={uploading} />
          <p className="font-OpenSansRegular text-[13px] font-[400] text-subTitleColor">
            You can upload images up to 2MB
          </p>
        </div>
      </div>

      <div className="mt-10">
        <span className="font-OpenSansRegular text-[16px] font-[600] text-titleColor">PUSH NOTIFICATIONS</span>
        <div className="flex items-center justify-between mt-3 max-w-[420px]">
          <p className="font-OpenSansMedium text-[13px] font-[500] text-titleColor">
            Receive push notifications in this browser
          </p>
          <button
            type="button"
            role="switch"
            aria-checked={pushEnabled}
            disabled={pushToggling}
            onClick={togglePushNotification}
            className={`relative inline-flex h-[24px] w-[44px] items-center rounded-full transition-colors duration-200 disabled:opacity-50 ${
              pushEnabled ? "bg-blue-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-[18px] w-[18px] transform rounded-full bg-white transition-transform duration-200 ${
                pushEnabled ? "translate-x-[23px]" : "translate-x-[3px]"
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default Account;
